import { Directive, HostListener, Input, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

enum CustomValidator {
  PhoneNumber = 'PhoneNumber',
  SSN = 'SSN',
  Date = 'Date',
  DATE_OF_BIRTH = 'DateOfBirth',
  VALIDATE_FUTURE = 'ValidateFuture',
  VALIDATE_PAST = 'ValidatePast',
  LOGIN_VALIDATOR_WHB = 'LoginValidatorWHB',
  LOGIN_VALIDATOR_SE = 'LoginValidatorSE',
  PLAN_SUMMARY_VALIDATOR_SE = 'PlanSummaryValidatorSE',
  MEDICARE_NUMBER_VALIDATOR = 'MedicareNumberValidator'
}

/**
 * Takes input from a textbox, if that textbox has specific validators (phone/date)
 * formats them as appropriate.  Allows the use of special keys
 */
@Directive({
    selector: '[appNumbersOnly]',
    standalone: false
})
export class ValidationsDirective implements OnInit, OnDestroy {
  //  Allow decimal numbers. The \. is only allowed once to occur
  // private regex: RegExp = new RegExp(/^[0-9]+(\.[0-9]*){0,1}$/g);
  @Input() regex: RegExp;
  @Input() customValidator: CustomValidator;

  //  Allow key codes for special events. Reflect :
  //  Backspace, tab, end, home
  private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home'];

  // Keep track of subscriber to remove it on destroy
  private $changesSub: Subscription;

  constructor(private el: ElementRef, public ngControl: NgControl) { }

  // Subscribe to the value changes method on initialization
  // {emitEvent: false} keeps the valueChanges from getting called again if a value needs updated
  ngOnInit(): void {
    this.$changesSub = this.ngControl.valueChanges.subscribe(newVal => {
      if (this.customValidator === CustomValidator.PhoneNumber) {
        this.ngControl.control.setValue(this.onPhoneInputChange(newVal, false), { emitEvent: false });
        this.ngControl.viewToModelUpdate(this.ngControl.control.value);

      } else if (this.customValidator === CustomValidator.Date ||
        this.customValidator === CustomValidator.DATE_OF_BIRTH ||
        this.customValidator === CustomValidator.VALIDATE_FUTURE ||
        this.customValidator === CustomValidator.VALIDATE_PAST) {
        this.ngControl.control.setValue(this.onDateInputChange(newVal, false), { emitEvent: false });
      } else if (this.customValidator === CustomValidator.SSN) {
        this.ngControl.control.setValue(this.onSsnInputChange(newVal, false), { emitEvent: false });
      }
    });
  }

  // Unsubscribe when destroyed
  ngOnDestroy(): void {
    this.$changesSub.unsubscribe();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {

    // Allow Backspace, tab, end, and home keys
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }

    if ([CustomValidator.VALIDATE_PAST,
    CustomValidator.VALIDATE_FUTURE,
    CustomValidator.DATE_OF_BIRTH,
    CustomValidator.Date].includes(this.customValidator)) {
      if (/[0-9/]/.test(event.key)
        && (this.el.nativeElement.value.length < 10
          || ((this.el.nativeElement.selectionEnd - this.el.nativeElement.selectionStart) > 0))) {
        return true;
      } else {
        event.preventDefault();
        return false;
      }
    }

    if ([CustomValidator.PhoneNumber].includes(this.customValidator)) {
      if (/[0-9()-]/.test(event.key)
        && (this.el.nativeElement.value.length < 14
          || ((this.el.nativeElement.selectionEnd - this.el.nativeElement.selectionStart) > 0))) {
        return true;
      } else {
        event.preventDefault();
        return false;
      }
    }

    const current: string = this.el.nativeElement.value;
    const next: string = current.concat(event.key);
    const newregex2: RegExp = new RegExp(this.regex);
    if (next && !String(next).match(newregex2)) {
      event.preventDefault();
    }
  }

  //  @HostListener('paste', ['$event']) onPaste(event) {
  //    // Don't allow pasted text that contains non-numerics
  //    if (this.customValidator === CustomValidator.PhoneNumber) {
  //      const pastedText = (event.originalEvent || event).clipboardData.getData('text/plain');
  //      if (pastedText) {
  //        const regEx = new RegExp('^[0-9]*$');
  //        if (!regEx.test(pastedText) || (this.maxlength && pastedText.length > this.maxlength) ||
  //          (this.min && +pastedText < this.min) ||
  //          (this.max && +pastedText >= this.max)) {
  //          event.preventDefault();
  //        }
  //      }
  //    }
  //  }

  @HostListener('keydown.backspace', ['$event'])
  keydownBackspace(event) {
    if (this.customValidator === CustomValidator.PhoneNumber) {
      this.ngControl.control.setValue(this.onPhoneInputChange(event.target.value, true));
    } else if (this.customValidator === CustomValidator.Date ||
      this.customValidator === CustomValidator.DATE_OF_BIRTH ||
      this.customValidator === CustomValidator.VALIDATE_FUTURE ||
      this.customValidator === CustomValidator.VALIDATE_PAST) {
      this.ngControl.control.setValue(this.onDateInputChange(event.target.value, true));
    } else if (this.customValidator === CustomValidator.SSN) {
      this.ngControl.control.setValue(this.onSsnInputChange(event, false));
    }

  }

  onPhoneInputChange(event, backspace) {
    if (event && event.replace) {
      let newVal = event.replace(/\D/g, '');
      if (backspace && newVal.length <= 6) {
        newVal = newVal.substring(0, newVal.length - 1);
      }
      if (newVal.length === 0) {
        newVal = '';
      } else if (newVal.length <= 3) {
        newVal = newVal.replace(/^(\d{0,3})/, '($1)');
      } else if (newVal.length <= 6) {
        newVal = newVal.replace(/^(\d{0,3})(\d{0,3})/, '($1) $2');
      } else if (newVal.length <= 10) {
        newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
      } else {
        newVal = newVal.substring(0, 10);
        newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
      }
      return newVal;
    }
  }

  onSsnInputChange(event, backspace) {
    if (event && event.replace) {
      let newVal = event.replace(/\D/g, '');
      if (backspace && newVal.length <= 12) {
        newVal = newVal.substring(0, newVal.length - 1);
      }

      // 5.	Cannot begin with:
      // a.	000
      // b.	666
      // c.	900-999
      // 6.	Cannot be 9-repeating numbers
      // a.	EXAMPLE: 222-22-2222
      // 7.	Cannot be a number with consecutive zeros in any digit group
      // a.	EXAMPLE: 000-##-###, ###-00-####, ###-##-0000

      if (newVal.length === 0) {
        newVal = '';
      } else if (newVal.length <= 3) {
        newVal = newVal.replace(/^(\d{0,3})/, '$1');
      } else if (newVal.length <= 6) {
        newVal = newVal.replace(/^(\d{0,3})(\d{0,2})/, '$1-$2');
      } else if (newVal.length <= 11) {
        newVal = newVal.replace(/^(\d{0,3})(\d{0,2})(\d{0,4})/, '$1-$2-$3');
      } else {
        newVal = newVal.substring(0, 12);
        newVal = newVal.replace(/^(\d{0,3})(\d{0,2})(\d{0,4})/, '$1-$2-$3');
      }
      return newVal;
    }
  }

  onDateInputChange(event, backspace) {
    if (event) {
      let newVal = event.replace(/\D/g, '');
      if (backspace && newVal.length <= 6) {
        newVal = newVal.substring(0, newVal.length - 1);
      }
      if (newVal.length === 0) {
        newVal = '';
      } else if (newVal.length === 1) {
        newVal = newVal.replace(/^(\d{0,2})/, '$1');
      } else if (newVal.length === 2) {
        newVal = newVal.replace(/^(\d{0,2})/, '$1/');
      } else if (newVal.length === 3) {
        newVal = newVal.replace(/^(\d{0,2})(\d{0,2})/, '$1/$2');
      } else if (newVal.length === 4) {
        newVal = newVal.replace(/^(\d{0,2})(\d{0,2})/, '$1/$2/');
      } else if (newVal.length <= 8) {
        newVal = newVal.replace(/^(\d{0,2})(\d{0,2})(\d{0,4})/, '$1/$2/$3');
      } else {
        newVal = newVal.substring(0, 8);
        newVal = newVal.replace(/^(\d{0,2})(\d{0,2})(\d{0,4})/, '$1/$2/$3');
      }
      return newVal;
    }
  }
}
